import React, { useEffect, useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    Chip,
    useTheme,
    Pagination,
    TextField,
    InputAdornment
} from '@mui/material';
import {
    CalendarToday,
    Search
} from '@mui/icons-material';
import { Layout } from '@layout/Layout';
import useAttendanceStore from '@/store/Employee/AttendanceStore';

const HRAttendanceHistory: React.FC = () => {
    const theme = useTheme();
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const recordsPerPage = 10;

    const {
        attendanceHistory,
        pagination,
        loading,
        error,
        fetchAttendanceHistory,
        resetError
    } = useAttendanceStore();

    useEffect(() => {
        fetchAttendanceHistory(currentPage, recordsPerPage);
    }, [currentPage]);

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setCurrentPage(value);
    };

    const formatTime = (dateTime: string) => {
        if (!dateTime) return '-';
        const date = new Date(dateTime);
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    };

    const formatDate = (dateTime: string) => {
        if (!dateTime) return '-';
        const date = new Date(dateTime);
        return date.toLocaleDateString('en-US', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const calculateHoursMinutes = (totalHours: number) => {
        const hours = Math.floor(totalHours);
        const minutes = Math.round((totalHours - hours) * 60);
        return `${hours}h ${minutes}m`;
    };

    // Filter attendance history based on search term
    const filteredHistory = attendanceHistory.filter((record) => {
        const searchLower = searchTerm.toLowerCase();
        const dateStr = formatDate(record.attendance_date).toLowerCase();
        const remarksStr = record.remarks?.toLowerCase() || '';
        return dateStr.includes(searchLower) || remarksStr.includes(searchLower);
    });

    return (
        <Layout>
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                {/* Header */}
                <Box sx={{ mb: 4 }}>
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 'bold',
                            color: theme.palette.text.primary,
                            mb: 1
                        }}
                    >
                        Attendance History
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{ color: theme.palette.text.secondary }}
                    >
                        View your complete attendance records
                    </Typography>
                </Box>

                {/* Error Alert */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }} onClose={resetError}>
                        {error}
                    </Alert>
                )}

                {/* Search Bar */}
                <Box sx={{ mb: 3 }}>
                    <TextField
                        fullWidth
                        placeholder="Search by date or remarks..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            maxWidth: 500,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                            }
                        }}
                    />
                </Box>

                {/* Loading State */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress size={60} />
                    </Box>
                ) : filteredHistory.length > 0 ? (
                    <>
                        {/* Attendance Records */}
                        <Box sx={{ display: 'grid', gap: 2 }}>
                            {filteredHistory.map((record) => (
                                <Card
                                    key={record.id}
                                    sx={{
                                        border: `1px solid ${theme.palette.divider}`,
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            boxShadow: theme.shadows[4],
                                            transform: 'translateY(-2px)'
                                        }
                                    }}
                                >
                                    <CardContent>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <CalendarToday sx={{ color: theme.palette.primary.main }} />
                                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                    {formatDate(record.attendance_date)}
                                                </Typography>
                                            </Box>
                                            <Chip
                                                label={record.status}
                                                size="small"
                                                color={
                                                    record.status === 'present'
                                                        ? 'success'
                                                        : record.status === 'absent'
                                                        ? 'error'
                                                        : 'warning'
                                                }
                                                sx={{ textTransform: 'capitalize' }}
                                            />
                                        </Box>

                                        <Box sx={{
                                            display: 'grid',
                                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
                                            gap: 2,
                                            mb: record.remarks ? 2 : 0
                                        }}>
                                            <Box
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 2,
                                                    backgroundColor: theme.palette.success.main + '15',
                                                    border: `1px solid ${theme.palette.success.main}30`
                                                }}
                                            >
                                                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mb: 0.5 }}>
                                                    Punch In
                                                </Typography>
                                                <Typography variant="body1" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
                                                    {formatTime(record.punch_in)}
                                                </Typography>
                                            </Box>

                                            <Box
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 2,
                                                    backgroundColor: record.punch_out
                                                        ? theme.palette.error.main + '15'
                                                        : theme.palette.mode === 'dark'
                                                        ? theme.palette.grey[800]
                                                        : theme.palette.grey[100],
                                                    border: `1px solid ${record.punch_out
                                                        ? theme.palette.error.main + '30'
                                                        : theme.palette.divider}`
                                                }}
                                            >
                                                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mb: 0.5 }}>
                                                    Punch Out
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: record.punch_out
                                                            ? theme.palette.error.main
                                                            : theme.palette.text.secondary
                                                    }}
                                                >
                                                    {record.punch_out ? formatTime(record.punch_out) : '-'}
                                                </Typography>
                                            </Box>

                                            <Box
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 2,
                                                    backgroundColor: theme.palette.info.main + '15',
                                                    border: `1px solid ${theme.palette.info.main}30`
                                                }}
                                            >
                                                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mb: 0.5 }}>
                                                    Total Hours
                                                </Typography>
                                                <Typography variant="body1" sx={{ fontWeight: 600, color: theme.palette.info.main }}>
                                                    {record.total_hours ? calculateHoursMinutes(Number(record.total_hours)) : '-'}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {record.remarks && (
                                            <Box
                                                sx={{
                                                    p: 2,
                                                    backgroundColor: theme.palette.background.default,
                                                    borderRadius: 2,
                                                    border: `1px solid ${theme.palette.divider}`
                                                }}
                                            >
                                                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mb: 0.5 }}>
                                                    Daily Update:
                                                </Typography>
                                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                    {record.remarks}
                                                </Typography>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>

                        {/* Pagination */}
                        {pagination && pagination.totalPages > 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                                <Pagination
                                    count={pagination.totalPages}
                                    page={currentPage}
                                    onChange={handlePageChange}
                                    color="primary"
                                    size="large"
                                    showFirstButton
                                    showLastButton
                                />
                            </Box>
                        )}
                    </>
                ) : (
                    <Card sx={{ border: `1px solid ${theme.palette.divider}`, textAlign: 'center', py: 8 }}>
                        <CardContent>
                            <CalendarToday sx={{ fontSize: 64, color: theme.palette.text.disabled, mb: 2 }} />
                            <Typography variant="h6" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
                                No Attendance Records Found
                            </Typography>
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                {searchTerm ? 'Try adjusting your search criteria' : 'Your attendance history will appear here'}
                            </Typography>
                        </CardContent>
                    </Card>
                )}
            </Container>
        </Layout>
    );
};

export default HRAttendanceHistory;
